import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { api } from "../utils/api";
import { t } from "../utils/i18n";
import { formatScore } from "../utils/format";
import ScoreBadge from "../components/ScoreBadge";

const CRITERIA = [
  { key: "ponctualite", label: "Ponctualité", desc: "Est arrivé à l'heure" },
  { key: "respect", label: "Respect & bienveillance", desc: "Attitude agréable et respectueuse" },
  { key: "ambiance", label: "Bonne humeur", desc: "A contribué à une bonne ambiance" },
  { key: "fiabilite", label: "Fiabilité", desc: "S'est présenté et a tenu ses engagements" },
  { key: "recommandation", label: "Recommandation", desc: "Je recommande cette personne" },
];

const LOW_SCORE_REASONS = ["En retard sans prévenir","Comportement distant ou froid","N'a pas respecté les règles du lieu","Communication difficile","Pas à la hauteur de son profil","Autre raison"];
const REPORT_REASONS = ["Harcèlement ou intimidation","Comportement agressif","Propos inappropriés ou offensants","Non respect des règles de sécurité","Comportement menaçant","Autre problème grave"];
const SERIOUS_REASONS = ["Agression physique","Agression verbale grave","Harcèlement sexuel","Comportement dangereux","Menaces","Autre, nécessite une intervention"];

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

// Eyebrow de section — même traitement que le Centre des règles (gris, majuscules, espacé),
// pour que cette page partage la même identité visuelle que le reste du produit.
const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "#999", marginBottom: 14 }}>{children}</p>
);

// Sélecteur 1-10 plus affirmé (boutons plus grands, sélection plus contrastée) — même mécanique
// de notation qu'avant, juste une exécution visuelle plus soignée.
function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as const }}>
      {[1,2,3,4,5,6,7,8,9,10].map(n => (
        <button key={n} onClick={() => onChange(n)} style={{
          flex: 1, minWidth: 32, padding: "11px 0", borderRadius: 12,
          border: value === n ? "none" : "1.5px solid #E5E5E5",
          backgroundColor: value === n ? "#0a0a0a" : "#FAFAFA",
          color: value === n ? "white" : "#666",
          fontSize: 13.5, fontWeight: value === n ? 800 : 500,
          cursor: "pointer", transition: "all 0.15s",
        }}>{n}</button>
      ))}
    </div>
  );
}

type RateablePerson = { id: string; name: string; avatar_url?: string; score?: number };
type RateableGroup = {
  kind: "publication" | "date";
  publication_id?: string; date_invitation_id?: string;
  publication_title?: string; title: string; event_date: string; people: RateablePerson[];
};
type Target = { kind: "publication" | "date"; targetId: string; title: string; person: RateablePerson };
type ReceivedGroup = {
  publication_id: string; publication_title: string; event_date: string; ratings_count: string;
  avg_score: string; avg_ponctualite: string; avg_respect: string; avg_ambiance: string; avg_fiabilite: string; avg_recommandation: string;
};
type Breakdown = {
  passions_score: string | null; passions_count: number;
  powers_score: string | null; powers_count: number;
  dates_score: string | null; dates_count: number;
};

const card: React.CSSProperties = { backgroundColor: "white", borderRadius: 20, padding: 22, marginBottom: 14, border: "1px solid #EFEFEF" };

export default function Rating() {
  const { user, lang } = useApp();
  const [loading, setLoading] = useState(true);
  const [rateable, setRateable] = useState<RateableGroup[]>([]);
  const [received, setReceived] = useState<ReceivedGroup[]>([]);
  const [breakdown, setBreakdown] = useState<Breakdown | null>(null);
  const [target, setTarget] = useState<Target | null>(null);

  const [scores, setScores] = useState<Record<string, number>>({});
  const [lowScoreReason, setLowScoreReason] = useState("");
  const [reportReason, setReportReason] = useState("");
  const [seriousReason, setSeriousReason] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justRated, setJustRated] = useState<{ person: RateablePerson; avgScore: number; needsReport: boolean; needsSerious: boolean } | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/ratings/rateable").catch(() => ({ rateable: [] })),
      api.get("/ratings/received").catch(() => ({ received: [] })),
      api.get("/users/me").catch(() => null),
    ]).then(([r, rec, me]) => {
      setRateable(r?.rateable || []);
      setReceived(rec?.received || []);
      setBreakdown(me?.social_score_breakdown || null);
    }).finally(() => setLoading(false));
  }, []);

  const avgScore = Object.values(scores).length > 0
    ? Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length * 10) / 10
    : 0;

  const allRated = CRITERIA.every(c => scores[c.key] > 0);
  const needsLow = avgScore > 0 && avgScore < 8;
  const needsReport = avgScore > 0 && avgScore < 6;
  const needsSerious = avgScore > 0 && avgScore < 4;

  const canSubmit = allRated &&
    (!needsLow || lowScoreReason !== "") &&
    (!needsReport || reportReason !== "") &&
    (!needsSerious || seriousReason !== "");

  const openTarget = (kind: "publication" | "date", targetId: string, title: string, person: RateablePerson) => {
    setTarget({ kind, targetId, title, person });
    setScores({});
    setLowScoreReason(""); setReportReason(""); setSeriousReason(""); setComment("");
  };

  const submitRating = () => {
    if (!target || !canSubmit) return;
    setSubmitting(true);
    api.post("/ratings", {
      rated_id: target.person.id,
      ...(target.kind === "date" ? { date_invitation_id: target.targetId } : { publication_id: target.targetId }),
      ...scores,
      low_score_reason: lowScoreReason || null,
      report_reason: reportReason || null,
      serious_reason: seriousReason || null,
      comment: comment || null,
    }).then(data => {
      if (data?.error) { alert(data.error); return; }
      setJustRated({ person: target.person, avgScore, needsReport, needsSerious });
      setRateable(prev => prev
        .map(g => {
          const gId = g.kind === "date" ? g.date_invitation_id : g.publication_id;
          return gId === target.targetId ? { ...g, people: g.people.filter(p => p.id !== target.person.id) } : g;
        })
        .filter(g => g.people.length > 0));
      setTarget(null);
    }).catch(() => alert("Erreur de connexion")).finally(() => setSubmitting(false));
  };

  // ─── Confirmation post-envoi ────────────────────────────────────────────
  if (justRated) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100%", backgroundColor: "#FAFAFA", padding: 24 }}>
        <div style={{ ...card, maxWidth: 440, width: "100%", textAlign: "center", padding: "40px 32px" }}>
          <div style={{ width: 96, height: 96, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", flexDirection: "column" }}>
            <span style={{ fontSize: 32, fontWeight: 800, color: "white", letterSpacing: -0.5 }}>{justRated.avgScore}</span>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>/10</span>
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 10, letterSpacing: -0.3 }}>Évaluation envoyée</h2>
          <p style={{ fontSize: 14, color: "#666", lineHeight: 1.7, marginBottom: 20 }}>
            Ton évaluation de {justRated.person.name} a bien été enregistrée. Elle sera prise en compte de façon anonyme dans son score social.
          </p>
          {justRated.needsSerious && (
            <div style={{ backgroundColor: "#F5F5F5", borderRadius: 14, padding: 16, marginBottom: 16, textAlign: "left" }}>
              <span style={{ fontSize: 13, color: "#0a0a0a", lineHeight: 1.6, fontWeight: 500 }}>Ton signalement grave a été transmis à l'équipe OLTODO. Nous te contacterons sous 24h.</span>
            </div>
          )}
          {justRated.needsReport && !justRated.needsSerious && (
            <div style={{ backgroundColor: "#F5F5F5", borderRadius: 14, padding: 16, marginBottom: 16, textAlign: "left" }}>
              <span style={{ fontSize: 13, color: "#666", lineHeight: 1.6, fontWeight: 500 }}>Ton signalement a été enregistré. L'équipe OLTODO examinera ce profil.</span>
            </div>
          )}
          <button onClick={() => setJustRated(null)} style={{ width: "100%", backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 16, padding: "15px 0", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
            Continuer
          </button>
        </div>
      </div>
    );
  }

  // ─── Formulaire de notation d'une personne précise ─────────────────────
  if (target) {
    return (
      <div style={{ padding: "24px 16px", maxWidth: 560, margin: "0 auto" }}>
        <button onClick={() => setTarget(null)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontSize: 13.5, fontWeight: 700, color: "#0a0a0a", marginBottom: 24, padding: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Retour
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
          <div style={{ width: 54, height: 54, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, overflow: "hidden", flexShrink: 0 }}>
            {target.person.avatar_url ? <img src={target.person.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(target.person.name)}
          </div>
          <div>
            <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: -0.3 }}>Noter {target.person.name}</div>
            <div style={{ fontSize: 12.5, color: "#999" }}>{target.kind === "date" ? `${t(lang, "date_meeting_label")}, ${target.title}` : target.title}</div>
          </div>
        </div>

        {CRITERIA.map(c => (
          <div key={c.key} style={{ ...card, marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{c.label}</div>
            <div style={{ fontSize: 12, color: "#999", marginBottom: 10 }}>{c.desc}</div>
            <StarPicker value={scores[c.key] || 0} onChange={v => setScores(prev => ({ ...prev, [c.key]: v }))} />
          </div>
        ))}

        {needsLow && (
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Qu'est-ce qui n'allait pas ?</div>
            {LOW_SCORE_REASONS.map(r => (
              <div key={r} onClick={() => setLowScoreReason(r)} style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${lowScoreReason === r ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: lowScoreReason === r ? "#F0F0F0" : "white", marginBottom: 8, cursor: "pointer", fontSize: 13 }}>{r}</div>
            ))}
          </div>
        )}

        {needsReport && (
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: "#c0392b" }}>Signaler un comportement problématique</div>
            {REPORT_REASONS.map(r => (
              <div key={r} onClick={() => setReportReason(r)} style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${reportReason === r ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: reportReason === r ? "#F0F0F0" : "white", marginBottom: 8, cursor: "pointer", fontSize: 13 }}>{r}</div>
            ))}
          </div>
        )}

        {needsSerious && (
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: "#c0392b" }}>Signalement grave, équipe OLTODO alertée</div>
            {SERIOUS_REASONS.map(r => (
              <div key={r} onClick={() => setSeriousReason(r)} style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${seriousReason === r ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: seriousReason === r ? "#F0F0F0" : "white", marginBottom: 8, cursor: "pointer", fontSize: 13 }}>{r}</div>
            ))}
          </div>
        )}

        <div style={card}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Commentaire (optionnel)</div>
          <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Un mot sur cette expérience..." style={{ width: "100%", minHeight: 70, padding: 10, borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
        </div>

        <button onClick={submitRating} disabled={!canSubmit || submitting} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "none", backgroundColor: canSubmit ? "#0a0a0a" : "#E0E0E0", color: "white", fontSize: 15, fontWeight: 700, cursor: canSubmit ? "pointer" : "not-allowed", marginTop: 4 }}>
          {submitting ? "Envoi..." : "Envoyer l'évaluation"}
        </button>
      </div>
    );
  }

  if (loading) {
    return <div style={{ textAlign: "center", padding: 60, color: "#999" }}>Chargement…</div>;
  }

  const nothingAtAll = rateable.length === 0 && received.length === 0;

  // ─── Page principale : score, à noter (si besoin), notes reçues ────────
  return (
    <div style={{ padding: "24px 16px", maxWidth: 560, margin: "0 auto" }}>

      {/* En-tête score — même anneau que la carte "Score Social" du profil/de l'accueil,
          plutôt qu'un rond plein avec un pictogramme (ancien traitement, incohérent avec le
          reste du produit). */}
      <div style={{ textAlign: "center", marginBottom: 36 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><ScoreBadge score={user?.score} size={104} /></div>
        <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "#999", marginBottom: 14 }}>Score Social {t(lang, "score_social_subtitle")}</p>
        <p style={{ fontSize: 14, color: "#666", lineHeight: 1.7, maxWidth: 420, margin: "0 auto" }}>
          {t(lang, "score_social_description")}
        </p>
      </div>

      {/* À noter */}
      {rateable.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <Eyebrow>{t(lang, "rateable_section_title")}</Eyebrow>
          {rateable.map(group => {
            const groupId = group.kind === "date" ? group.date_invitation_id! : group.publication_id!;
            return (
              <div key={groupId} style={card}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>
                  {group.kind === "date" ? t(lang, "date_meeting_label") : group.title}
                </div>
                {group.kind === "date" && <div style={{ fontSize: 12, color: "#666", marginBottom: 2 }}>{group.title}</div>}
                <div style={{ fontSize: 11, color: "#999", marginBottom: 12 }}>{new Date(group.event_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</div>
                {group.people.map(person => (
                  <div key={person.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "1px solid #F8F8F8" }}>
                    <div style={{ width: 38, height: 38, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12, overflow: "hidden", flexShrink: 0 }}>
                      {person.avatar_url ? <img src={person.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(person.name)}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{person.name}</span>
                    <button onClick={() => openTarget(group.kind, groupId, group.title, person)} style={{ backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 16, padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Noter</button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Sous-scores Passions / Connections / Powers — juste avant les notes reçues */}
      {breakdown && (breakdown.passions_count > 0 || breakdown.powers_count > 0) && (
        <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
          <div style={{ ...card, flex: 1, textAlign: "center", marginBottom: 0, padding: "16px 8px" }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{breakdown.passions_count > 0 ? formatScore(breakdown.passions_score || 0) : "—"}</div>
            <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>Passions</div>
          </div>
          {breakdown.dates_count > 0 && (
            <div style={{ ...card, flex: 1, textAlign: "center", marginBottom: 0, padding: "16px 8px" }}>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{formatScore(breakdown.dates_score || 0)}</div>
              <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>Connections</div>
            </div>
          )}
          <div style={{ ...card, flex: 1, textAlign: "center", marginBottom: 0, padding: "16px 8px" }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{breakdown.powers_count > 0 ? formatScore(breakdown.powers_score || 0) : "—"}</div>
            <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>Powers</div>
          </div>
        </div>
      )}

      {/* Notes reçues — jamais l'identité de qui a noté, uniquement le résultat agrégé */}
      {received.length > 0 && (
        <div>
          <Eyebrow>Notes reçues</Eyebrow>
          {received.map(ev => (
            <div key={ev.publication_id} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{ev.publication_title}</div>
                  <div style={{ fontSize: 11, color: "#999" }}>
                    {new Date(ev.event_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                    {" · "}{ev.ratings_count} avis
                  </div>
                </div>
                <div style={{ backgroundColor: "#0a0a0a", color: "white", borderRadius: 20, padding: "4px 12px", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                  {formatScore(ev.avg_score)}/10
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" as const, fontSize: 11, color: "#666" }}>
                <span>Ponctualité {formatScore(ev.avg_ponctualite)}</span>
                <span>Respect {formatScore(ev.avg_respect)}</span>
                <span>Ambiance {formatScore(ev.avg_ambiance)}</span>
                <span>Fiabilité {formatScore(ev.avg_fiabilite)}</span>
                <span>Recommandation {formatScore(ev.avg_recommandation)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rien du tout — explication complète pour un nouvel arrivant */}
      {nothingAtAll && (
        <div style={{ textAlign: "center", padding: "20px 8px" }}>
          <div style={{ backgroundColor: "#F0F0F0", borderRadius: 16, padding: 20, marginBottom: 20, textAlign: "left" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0a0a0a", marginBottom: 8 }}>Comment ça marche ?</div>
            <div style={{ fontSize: 13, color: "#555", lineHeight: 1.8 }}>
              1. Participez à une activité publiée sur OLTODO<br/>
              2. Une fois l'activité terminée, vous recevez une notification<br/>
              3. Notez chaque participant sur 5 critères (ponctualité, respect...)<br/>
              4. Votre Score Social évolue selon les notes que vous recevez
            </div>
          </div>
          <p style={{ fontSize: 13, color: "#999" }}>
            Rien à noter pour le moment, revenez ici après vos prochaines activités !
          </p>
        </div>
      )}
    </div>
  );
}
